import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ShieldCheck, Star } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';

const TutorDirectoryV4 = () => {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingButtons, setShowFloatingButtons] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    const fetchTutors = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/v4/public/tutors`);
        const data = await response.json();
        if (data.success) {
          setTutors(data.tutors || []);
        }
      } catch (error) {
        console.error('Error fetching tutors:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTutors();
  }, [BACKEND_URL]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin" style={{color: '#006C69'}} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Find a Tutor | Winhouse Tutors"
        description="Browse and connect with expert tutors for VCE, IELTS, and more."
        path="/tutors"
      />
      {/* Global Navigation - Same as Home, Services, Pricing, Become Tutor */}
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
      {/* Page Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-stone-50 py-12" style={{paddingTop: '6rem'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-3 text-gray-900">
              Our Expert Tutors
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              Find your perfect match from our premium tutor network
            </p>
          </div>
        </div>
      </div>
      
      {/* Tutor Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {tutors.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-600">No tutors available at the moment</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tutors.map(tutor => (
              <Card 
                key={tutor.id} 
                className="group hover:scale-[1.02] transition-all duration-300 cursor-pointer bg-white"
                style={{
                  border: '1px solid rgba(183, 191, 114, 0.20)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.10), 0 0 0 1px rgba(183, 191, 114, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                }}
                onClick={() => navigate(`/tutors/${tutor.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-2xl font-bold text-gray-900" style={{fontSize: '1.65rem', fontWeight: '700'}}>
                      {tutor.directory_display_name?.trim() || tutor.full_name}
                    </CardTitle>
                    <Badge className="text-white hover:opacity-90 flex items-center gap-1 px-3 py-1" style={{background: '#006C69', boxShadow: '0 1px 3px rgba(0, 108, 105, 0.2)'}}>
                      <ShieldCheck className="w-3 h-3" />
                      Verified
                    </Badge>
                  </div>
                  
                  {tutor.experience && (
                    <div className="flex items-center gap-1 text-sm font-medium" style={{color: '#006C69'}}>
                      <Star className="w-4 h-4 fill-current" />
                      <span>{tutor.experience} experience</span>
                    </div>
                  )}
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {tutor.subjects && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-2">Subjects:</p>
                      <div className="flex flex-wrap gap-2">
                        {(Array.isArray(tutor.subjects) 
                          ? tutor.subjects 
                          : tutor.subjects.split(',')
                        ).slice(0, 3).map((subject, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline"
                            className="text-xs"
                            style={{
                              borderColor: 'rgba(183, 191, 114, 0.30)',
                              color: '#333333',
                              background: 'rgba(183, 191, 114, 0.08)',
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.75rem'
                            }}
                          >
                            {typeof subject === 'string' ? subject.trim() : subject}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {tutor.bio && (
                    <p className="text-sm text-gray-600 line-clamp-3">
                      {tutor.bio}
                    </p>
                  )}
                  
                  <div className="pt-4 border-t">
                    <Button 
                      className="w-full font-semibold shadow-md group-hover:shadow-xl transition-all duration-300"
                      style={{
                        background: '#B7BF72',
                        color: '#000000',
                        transform: 'translateY(0)',
                        boxShadow: '0 2px 8px rgba(183, 191, 114, 0.25)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#000000';
                        e.currentTarget.style.color = '#FFFFFF';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.20)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#B7BF72';
                        e.currentTarget.style.color = '#000000';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(183, 191, 114, 0.25)';
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/enquire-v4', { 
                          state: { 
                            tutorId: tutor.id,
                            tutorName: tutor.full_name 
                          }
                        });
                      }}
                    >
                      Book Free Consultation
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Global Footer - Same as Home, Services, Pricing, Become Tutor */}
      <Footer />
      
      {/* Shared Floating Action Buttons (Book Free Trial + WhatsApp) */}
      <FloatingActionButtons />
    </div>
  );
};

export default TutorDirectoryV4;